import { useEffect, useMemo, useState } from "react";
import {
  EntryForm,
  Column,
  Input,
  CheckBox,
  TextArea,
} from "../components/EntryForm";
import Page from "../components/Page";

import { GetOffer, UpdateOffer } from "../services/api/offer";
import { AddAppResource, GetAppResource } from "../services/api/app";
import { useUserProvider } from "../providers/UserProvider";
import { useModalProvider } from "../providers/ModalProvider";
import { useLoaderProvider } from "../providers/LoaderProvider";
import MediaUploader from "../components/MediaUploader";
import { validations } from "../lib/form";

import { useFormik } from "formik";

import { useHistory } from "react-router-dom";

import * as Yup from "yup";
import responseProcessor from "../lib/responseProcessor";
import DataSelector from "../components/DataSelector";
import { Radio } from "antd";
import LimitReached from "../components/modal_contents/LimitReached";

const OfferDetail = ({ newOffer, match }) => {
  const vals = {
    title: "",
    message: "",
    video_url: "",
    image_url: "",
    unlock_code: "",
    action_bar_text: "",
    action_bar_url: "",
    email: "",
    send_email: false,
    media_metadata: {},
    active: false,
    enterprise_app: null,
    cta_type: "URL CTA",
    cta: null
  };

  const [initialValues, setInitialValues] = useState(vals);
  const [metadata, setMetadata] = useState({});
  const [data, setData] = useState({
    loading: false,
    data: [],
    count: 0,
    meta_data: {},
  });

  const [isLoading, setIsLoading] = useState(false);

  const { user } = useUserProvider();

  const { showModal, closeModal } = useModalProvider();

  const { doSetShowLoadingModal } = useLoaderProvider();

  const uuid = newOffer ? user.currentApp() : match.params.uuid;

  const history = useHistory();

  const showBusiness = useMemo(() => (user?.app_data?.setup?.enterprise && user?.app_data?.setup?.enterprise_launch_business), [user]);
  
  const loadData = async () => {
    setData({ ...data, loading: true });
    GetAppResource(user, "offers")
      .then((res) => res.json())
      .then((jsonData) => {
        setData({
          loading: false,
          data: jsonData.data,
          count: jsonData.data.length,
          meta_data: jsonData?.meta_data,
        });
      });
  };

  useEffect(() => {
    if (newOffer) {
      setInitialValues(vals);
    } else {
      setIsLoading(true);
      GetOffer(uuid, user.idToken)
        .then((res) => res.json())
        .then((res) => {
          setInitialValues({
            title: res.data.title,
            message: res.data.message,
            video_url: res.data.video_url,
            image_url: res.data.image_url,
            unlock_code: res.data.unlock_code,
            action_bar_text: res.data.action_bar_text,
            action_bar_url: res.data.action_bar_url,
            email: res.data.email,
            send_email: res.data.send_email,
            active: res.data.active,
            enterprise_app: res?.data?.enterprise_app,
            cta_type: res?.data?.cta_type,
            cta: res?.data?.cta
          });
          setMetadata(res.data.media_metadata);
          setIsLoading(false);
        });
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    doSetShowLoadingModal(isLoading);
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentAction = newOffer ? "New" : "Edit";
  const currentUrl = newOffer ? "/offers/new" : `/offers/${uuid}`;

  let links = [
    { name: "Home", url: "/" },
    { name: "Special Offer Codes", url: "/offers" },
    { name: `${currentAction} Special Offer Code`, url: currentUrl },
  ];

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: () =>
      Yup.lazy((values) => {
        let schema = {
          title: validations.str_req_60,
          message: Yup.string().required("Required"),
          video_url: validations.str_255,
          image_url: validations.str_255,
          action_bar_text: validations.str_notreq_50,
          action_bar_url: validations.str_255,
          email: Yup.string()
            .email("Invalid email")
            .max(255, "Maximum 255 Characters"),
          send_email: Yup.boolean().nullable(),
          enterprise_app: Yup.object({
            id: Yup.string().nullable(),
            name: Yup.string().nullable(),
          }).nullable(),
          cta_type: Yup.string().required("Required"),
          cta: Yup.object({
            id: Yup.string().nullable(),
            name: Yup.string().nullable(),
          }).nullable(),
        };
        if (values?.send_email) {
          schema.email = Yup.string()
            .email("Invalid email")
            .max(255, "Maximum 255 Characters")
            .required("Required");
        }
        return Yup.object().shape(schema);
      }),
    onSubmit: async (values) => {
      delete values.unlock_code;
      setIsLoading(true)

      if (["URL CTA", "Launch Business"].includes(values?.cta_type)) {
        values.cta = {}
      }

      if (["Preset CTA", "URL CTA"].includes(values?.cta_type)) {
        values.enterprise_app = {}
      }

      if (newOffer) {
        AddAppResource(uuid, "offers", user.idToken, { data: values }).then(
          (response) => responseProcessorProxy(response, () => {
            const shouldShowLimit =
              user?.app_data?.portal?.free_account && data?.meta_data?.total >= data?.meta_data?.limit;
            if (shouldShowLimit) {
              showModal(
                <LimitReached
                  onClose={closeModal}
                  freeMessage={user?.app_data?.portal?.free_account_message}
                  appId={user?.currentApp()}
                />,
                {
                  maxHeight: "70vh",
                  maxWidth: "50vw",
                }
              );
            }
          })
        );
      } else {
        UpdateOffer(uuid, user.idToken, { data: values }).then((response) => { 
          responseProcessorProxy(response)
        }
          
        );
      }
    },
  });

  const handleSuccess = () => {
    setIsLoading(false);
    history.push("/offers")
  };
  
  const handleError = () => {
    setIsLoading(false);
  };

  const responseProcessorProxy = (response, additionalOnSuccess) => {
    responseProcessor({
      response,
      showModal,
      onSuccess: () => {
        handleSuccess();
        if (additionalOnSuccess) {
          additionalOnSuccess();
        }
      },
      onError: handleError,
    });
  };

  return (
    <Page links={links} title={`${currentAction} Special Offer Code`}>
      <EntryForm
        isLoading={isLoading}
        formik={formik}
        submitHandler={formik.handleSubmit}
        cancelURL="/offers"
      >
        <Column>
          <Input name="title" type="text" label="Title" formik={formik} />
          {!newOffer && (
            <Input
              name="unlock_code"
              type="text"
              label="Unlock Code"
              formik={formik}
              disabled
            />
          )}
          <TextArea
            name="message"
            type="text"
            label="Message"
            formik={formik}
          />
          <MediaUploader
            name="video_url"
            type="text"
            label="Video URL"
            formik={formik}
            metadata={metadata}
            mediaType="video"
          />
          <MediaUploader
            name="image_url"
            type="text"
            label="Image URL"
            formik={formik}
            metadata={metadata}
            mediaType="image"
          />
        </Column>
        <Column>
        <div className="flex flex-row w-full justify-between pb-4">
          <label
            className={`text-lg mb-5`}
            htmlFor={"cta_type"}
          >
            CTA Type
            <span style={{ color: "red" }}> *</span>
          </label>
          <Radio.Group
            formik={formik}
            className="flex flex-row"
            value={formik?.values?.cta_type ?? "URL CTA"}
            onChange={event => {
              formik.setFieldValue("cta_type", event.target.value)
              formik.setFieldValue("cta", null)
              formik.setFieldValue("action_bar_text", "")
              formik.setFieldValue("action_bar_url", "")
              formik.setFieldValue("enterprise_app", null)
            }}
          >
            <Radio
              value="URL CTA"
            >
              URL CTA
            </Radio>
            <Radio
              value="Preset CTA"
            >
              Preset CTA
            </Radio>
            {
              user?.app_data?.setup.enterprise_launch_business ? (
                <Radio
                  value="Launch Business"
                >
                  Launch Business
                </Radio>
              ) : null
            }
          </Radio.Group>
        </div>
          {
            !["Preset CTA"].includes(formik.values.cta_type) ? (
              <Input
                name="action_bar_text"
                type="text"
                label={`${formik.values.cta_type === "Launch Business" && showBusiness ? "Launch Business" : ""} Call to Action Text`}
                formik={formik}
              />
            ) : null
          }
          {
            formik.values.cta_type === "Launch Business" && showBusiness ? (
              <DataSelector
                name="enterprise_app"
                formik={formik}
                label="Which Business?"
                resourceEndpoint="children"
                filterOptions={(options) => {
                  return options.filter((a) => a.active );
                }}
                single  
                singleSelectOnly
                withSearch
              />
            ) : null
          }

          {
            !["Preset CTA", "Launch Business"].includes(formik.values.cta_type) ? (
              <Input
                name="action_bar_url"
                type="text"
                label="Call to Action URL"
                formik={formik}
              />
            ) : null
          }

          {
            formik.values.cta_type === "Preset CTA" ? (
              <DataSelector
                name="cta"
                formik={formik}
                label="Call to Action Name List"
                resourceEndpoint="call_to_actions"
                single
                singleSelectOnly
                withSearch
              />
            ) : null
          }

          <Input
            name="email"
            type="email"
            label="Notification Email"
            formik={formik}
          />
          <div className="flex">
            <span className="flex w-2/3">
              <CheckBox name="send_email" label="Send Email" formik={formik} />
            </span>
          </div>
          <CheckBox name="active" label="Active" formik={formik} />
        </Column>
      </EntryForm>
    </Page>
  );
};

export default OfferDetail;
